// Extracted from original/src/App.tsx (ADR-08 §2.1 "copy, then cut logic"):
//   AddCapitalModal       :44876-44962  (إضافة رأس مال جديد)
//   WithdrawCapitalModal  :44963-45038  (سحب من رأس المال)
//   CapitalCardBody       :15415-15536  (Settings card "رأس المال المستثمر": add / withdraw tiles + reset section)
// Kept: every class string, header colour, icon and size, the backdrop and the motion frames, the field order, the
// explanation boxes, the submit buttons and their labels; the card's two tiles and the reset section's frame.
// Cut: `handleAddCapital` / `handleWithdrawCapital` → `onSubmit` (the container posts `capital_add` / `capital_withdraw`
// through the gateway: preview → PIN → commit, D-018, D-232; old: the Super code, App.tsx:8909-8990, 5574-5646).
// Changed by decision: the reset section's three buttons — "تصفير الخزنة", "تصفير الصناديق" (D-076: replaced by one
// "withdraw full balance as capital withdrawal" for a chosen vault or box) and "تصفير كافة الحسابات" (D09 §5.4: the
// factory reset has a single entry point in the maintenance card) — become the one D-076 button (V-51).
// Added: `FullBalanceWithdrawModal` (D-076; the withdraw dialog's frame and colours, a box picker with its ledger
// balance instead of the amount field), data-testid attributes.
import { AnimatePresence, motion } from 'motion/react';
import { Plus, RefreshCw, TrendingDown, X } from 'lucide-react';
import type { FormEvent } from 'react';
import { NumericInput } from '../components/NumericInput.js';
import { useI18n } from '../i18n-context.js';

export interface CapitalValues {
  readonly amount: string;
  readonly note: string;
}

export interface CapitalModalProps {
  readonly open: boolean;
  readonly values: CapitalValues;
  readonly onChange: (v: CapitalValues) => void;
  readonly busy: boolean;
  readonly onSubmit: () => void;
  readonly onClose: () => void;
}

const submit = (fn: () => void) => (e: FormEvent) => {
  e.preventDefault();
  fn();
};

export function AddCapitalModal(p: CapitalModalProps) {
  const { t } = useI18n();
  return (
    <AnimatePresence>
      {p.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="add-capital-modal">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={p.onClose} className="absolute inset-0 bg-black/60 backdrop-blur-md" />
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white dark:bg-[#0d1117] w-full max-w-md rounded-3xl shadow-2xl relative z-10 overflow-hidden border border-gray-100 dark:border-[#30363d] dark:digital-border"
          >
            <div className="p-6 border-b border-gray-100 dark:border-[#30363d] flex justify-between items-center bg-emerald-900 text-white">
              <h3 className="text-xl font-black flex items-center gap-2 uppercase tracking-tight">
                <Plus className="text-emerald-400" />
                {t('cap.add_title')}
              </h3>
              <button onClick={p.onClose} className="text-emerald-400 hover:text-white transition-colors">
                <X size={24} />
              </button>
            </div>
            <form onSubmit={submit(p.onSubmit)} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-bold text-gray-400 dark:text-[#8b949e] mb-2 uppercase tracking-widest">{t('cap.amount_iqd')}</label>
                <NumericInput
                  required
                  value={p.values.amount ?? ''}
                  onChange={(val) => p.onChange({ ...p.values, amount: val })}
                  className="w-full bg-gray-50 dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 text-xl font-black text-center dark:text-[#f0f6fc] font-mono transition-all"
                  placeholder="0"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-400 dark:text-[#8b949e] mb-2 uppercase tracking-widest">{t('cap.note_optional')}</label>
                <input
                  type="text"
                  value={p.values.note || ''}
                  onChange={(e) => p.onChange({ ...p.values, note: e.target.value })}
                  className="w-full bg-gray-50 dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 text-xs font-bold dark:text-[#f0f6fc]"
                  placeholder={t('cap.details_ph')}
                />
              </div>
              <div className="p-4 bg-emerald-50 dark:bg-emerald-900/10 rounded-2xl text-xs font-bold text-emerald-700 dark:text-emerald-400 border border-emerald-100 dark:border-emerald-900/30">{t('cap.add_explain')}</div>
              <button
                type="submit"
                disabled={p.busy}
                className="w-full py-4 bg-emerald-600 hover:bg-emerald-700 text-white rounded-2xl font-black uppercase tracking-widest shadow-lg shadow-emerald-600/20 transition-all cursor-pointer"
                data-testid="add-capital-submit"
              >
                {t('cap.add_submit')}
              </button>
            </form>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

export function WithdrawCapitalModal(p: CapitalModalProps) {
  const { t } = useI18n();
  return (
    <AnimatePresence>
      {p.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="withdraw-capital-modal">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={p.onClose} className="absolute inset-0 bg-black/60 backdrop-blur-md" />
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white dark:bg-[#0d1117] w-full max-w-md rounded-3xl shadow-2xl relative z-10 overflow-hidden border border-gray-100 dark:border-[#30363d] dark:digital-border"
          >
            <div className="p-6 border-b border-gray-100 dark:border-[#30363d] flex justify-between items-center bg-rose-900 text-white">
              <h3 className="text-xl font-black flex items-center gap-2 uppercase tracking-tight">
                <TrendingDown className="text-rose-400" />
                {t('cap.withdraw_title')}
              </h3>
              <button onClick={p.onClose} className="text-rose-400 hover:text-white transition-colors">
                <X size={24} />
              </button>
            </div>
            <form onSubmit={submit(p.onSubmit)} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-bold text-gray-400 dark:text-[#8b949e] mb-2 uppercase tracking-widest">{t('cap.amount_iqd')}</label>
                <NumericInput
                  required
                  value={p.values.amount ?? ''}
                  onChange={(val) => p.onChange({ ...p.values, amount: val })}
                  className="w-full bg-gray-50 dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-rose-500/20 text-xl font-black text-center dark:text-[#f0f6fc] font-mono transition-all"
                  placeholder="0"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-400 dark:text-[#8b949e] mb-2 uppercase tracking-widest">{t('cap.note_optional')}</label>
                <input
                  type="text"
                  value={p.values.note || ''}
                  onChange={(e) => p.onChange({ ...p.values, note: e.target.value })}
                  className="w-full bg-gray-50 dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-rose-500/20 text-xs font-bold dark:text-[#f0f6fc]"
                  placeholder={t('cap.details_ph')}
                />
              </div>
              <div className="p-4 bg-rose-50 dark:bg-rose-900/10 rounded-2xl text-xs font-bold text-rose-700 dark:text-rose-400 border border-rose-100 dark:border-rose-900/30">{t('cap.withdraw_explain')}</div>
              <button
                type="submit"
                disabled={p.busy}
                className="w-full py-4 bg-rose-600 hover:bg-rose-700 text-white rounded-2xl font-black uppercase tracking-widest shadow-lg shadow-rose-600/20 transition-all cursor-pointer"
                data-testid="withdraw-capital-submit"
              >
                {t('cap.withdraw_submit')}
              </button>
            </form>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

export interface FullBalanceBox {
  readonly id: string;
  readonly name: string;
  /** Ledger balance, formatted by the container ("750,100"). */
  readonly balanceLabel: string;
  /** False when the balance is not positive (nothing to withdraw). */
  readonly withdrawable: boolean;
}

export interface FullBalanceWithdrawModalProps {
  readonly open: boolean;
  readonly boxes: readonly FullBalanceBox[];
  readonly boxId: string;
  readonly note: string;
  readonly onChange: (v: { readonly boxId: string; readonly note: string }) => void;
  readonly busy: boolean;
  readonly onSubmit: () => void;
  readonly onClose: () => void;
}

/** D-076: the withdraw dialog's frame with a vault / box picker; the amount is the server's (the current ledger balance). */
export function FullBalanceWithdrawModal(p: FullBalanceWithdrawModalProps) {
  const { t } = useI18n();
  const chosen = p.boxes.find((b) => b.id === p.boxId);
  return (
    <AnimatePresence>
      {p.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="full-balance-modal">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={p.onClose} className="absolute inset-0 bg-black/60 backdrop-blur-md" />
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white dark:bg-[#0d1117] w-full max-w-md rounded-3xl shadow-2xl relative z-10 overflow-hidden border border-gray-100 dark:border-[#30363d] dark:digital-border"
          >
            <div className="p-6 border-b border-gray-100 dark:border-[#30363d] flex justify-between items-center bg-rose-900 text-white">
              <h3 className="text-xl font-black flex items-center gap-2 uppercase tracking-tight">
                <RefreshCw className="text-rose-400" />
                {t('cap.full_title')}
              </h3>
              <button onClick={p.onClose} className="text-rose-400 hover:text-white transition-colors">
                <X size={24} />
              </button>
            </div>
            <form onSubmit={submit(p.onSubmit)} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-bold text-gray-400 dark:text-[#8b949e] mb-2 uppercase tracking-widest">{t('cap.full_box')}</label>
                <select
                  required
                  value={p.boxId}
                  onChange={(e) => p.onChange({ boxId: e.target.value, note: p.note })}
                  className="w-full bg-gray-50 dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-rose-500/20 text-sm font-black dark:text-[#f0f6fc] transition-all"
                  data-testid="full-balance-box"
                >
                  <option value="">{t('cap.full_choose')}</option>
                  {p.boxes.map((b) => (
                    <option key={b.id} value={b.id} disabled={!b.withdrawable}>
                      {t('cap.full_option', { name: b.name, balance: b.balanceLabel })}
                    </option>
                  ))}
                </select>
              </div>
              <div className="w-full bg-gray-50 dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] rounded-xl px-4 py-3 text-xl font-black text-center dark:text-[#f0f6fc] font-mono" data-testid="full-balance-amount">
                {chosen ? `${chosen.balanceLabel} ${t('unit.iqd')}` : '0'}
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-400 dark:text-[#8b949e] mb-2 uppercase tracking-widest">{t('cap.note_optional')}</label>
                <input
                  type="text"
                  value={p.note}
                  onChange={(e) => p.onChange({ boxId: p.boxId, note: e.target.value })}
                  className="w-full bg-gray-50 dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-rose-500/20 text-xs font-bold dark:text-[#f0f6fc]"
                  placeholder={t('cap.details_ph')}
                />
              </div>
              <div className="p-4 bg-rose-50 dark:bg-rose-900/10 rounded-2xl text-xs font-bold text-rose-700 dark:text-rose-400 border border-rose-100 dark:border-rose-900/30">{t('cap.full_explain')}</div>
              <button
                type="submit"
                disabled={p.busy || !chosen?.withdrawable}
                className="w-full py-4 bg-rose-600 hover:bg-rose-700 text-white rounded-2xl font-black uppercase tracking-widest shadow-lg shadow-rose-600/20 transition-all cursor-pointer disabled:opacity-50"
                data-testid="full-balance-submit"
              >
                {t('cap.withdraw_submit')}
              </button>
            </form>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

export interface CapitalCardBodyProps {
  readonly onAdd: () => void;
  readonly onWithdraw: () => void;
  readonly onFullBalance: () => void;
}

/** App.tsx:15433-15536 — the body of the Settings card "رأس المال المستثمر" (the frame comes from `SettingsCardFrame`). */
export function CapitalCardBody(p: CapitalCardBodyProps) {
  const { t } = useI18n();
  return (
    <div className="space-y-6" data-testid="capital-card">
      {/* Capital Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={p.onAdd}
          data-testid="capital-add"
          className="p-6 bg-emerald-50 dark:bg-emerald-500/5 border border-emerald-100 dark:border-emerald-500/20 rounded-[2rem] flex flex-col items-center justify-center gap-3 hover:bg-emerald-100 dark:hover:bg-emerald-500/10 transition-all group shadow-sm"
        >
          <div className="p-4 bg-white dark:bg-[#0d1117] rounded-2xl shadow-sm border border-emerald-100 dark:border-emerald-500/30 group-hover:scale-110 transition-transform">
            <Plus className="text-emerald-600 dark:text-emerald-400 w-6 h-6" />
          </div>
          <div className="text-center">
            <span className="block text-xs font-black uppercase tracking-widest text-emerald-700 dark:text-emerald-400 mb-1">
              {t('addCapital')}
            </span>
            <span className="text-[9px] text-emerald-600/60 dark:text-emerald-400/40 font-bold uppercase tracking-tighter">
              {t('cap.add_sub')}
            </span>
          </div>
        </motion.button>
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={p.onWithdraw}
          data-testid="capital-withdraw"
          className="p-6 bg-rose-50 dark:bg-rose-500/5 border border-rose-100 dark:border-rose-500/20 rounded-[2rem] flex flex-col items-center justify-center gap-3 hover:bg-rose-100 dark:hover:bg-rose-500/10 transition-all group shadow-sm"
        >
          <div className="p-4 bg-white dark:bg-[#0d1117] rounded-2xl shadow-sm border border-rose-100 dark:border-rose-500/30 group-hover:scale-110 transition-transform">
            <TrendingDown className="text-rose-600 dark:text-rose-400 w-6 h-6" />
          </div>
          <div className="text-center">
            <span className="block text-xs font-black uppercase tracking-widest text-rose-700 dark:text-rose-400 mb-1">
              {t('withdrawCapital')}
            </span>
            <span className="text-[9px] text-rose-600/60 dark:text-rose-400/40 font-bold uppercase tracking-tighter">
              {t('cap.withdraw_sub')}
            </span>
          </div>
        </motion.button>
      </div>
      {/* Reset Section — D-076: one "withdraw full balance as capital withdrawal" button (V-51) */}
      <div className="p-6 bg-gray-50 dark:bg-[#05070a] rounded-[2rem] border border-gray-100 dark:border-[#30363d] space-y-4">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-1.5 h-6 bg-rose-500 rounded-full" />
          <label className="text-xs font-black text-gray-900 dark:text-[#f0f6fc] uppercase tracking-widest">
            {t('cap.reset_section')}
          </label>
        </div>
        <div className="grid grid-cols-1 gap-3">
          <button
            onClick={p.onFullBalance}
            data-testid="capital-full-balance"
            className="group w-full p-4 bg-white dark:bg-[#0d1117] border border-gray-200 dark:border-[#30363d] rounded-2xl flex items-center justify-between hover:border-blue-500/50 transition-all shadow-sm cursor-pointer"
          >
            <div className="flex items-center gap-4">
              <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded-xl text-blue-600 dark:text-blue-400 group-hover:rotate-180 transition-transform duration-500">
                <RefreshCw size={16} />
              </div>
              <span className="text-[10px] font-black uppercase tracking-widest text-gray-600 dark:text-gray-400">
                {t('cap.full_button')}
              </span>
            </div>
            <div className="w-2 h-2 rounded-full bg-blue-500/20 group-hover:bg-blue-500 transition-colors" />
          </button>
        </div>
      </div>
    </div>
  );
}
